'use client'

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/extended-button'
import { Input } from '@/components/ui/input'
import { Copy, MessageCircle, Loader2 } from 'lucide-react'
import {
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
} from 'react-icons/fa6'
import { SiGmail, SiMicrosoftoutlook } from 'react-icons/si'
import { toast } from 'react-toastify'
import { buildShareUrl } from '@/lib/sharing'
import { useLogShareMutation } from 'graphql/generated/graphql'

interface ProfileShareModalProps {
  open: boolean
  onClose: () => void
  profile: {
    name: string
    profilePath: string
    userType: string
    listingId?: number | null
    userId?: number | null
  }
  userSlug?: string | null
}

type ChannelKey = 'instagram' | 'facebook' | 'twitter' | 'linkedin' | 'gmail' | 'outlook' | 'sms'

export function ProfileShareModal({ open, onClose, profile, userSlug }: ProfileShareModalProps) {
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [igBusy, setIgBusy] = useState(false)
  const [logShare] = useLogShareMutation()

  useEffect(() => {
    setIsTouchDevice(window.matchMedia('(pointer: coarse)').matches)
  }, [])

  const rawType = profile.userType.toLowerCase()
  const contentType = rawType === 'general' ? 'member' : rawType === 'farm' ? 'producer' : rawType
  const contentId = String(profile.listingId || profile.userId || '')
  const shareUrl =
    typeof window !== 'undefined'
      ? buildShareUrl(profile.profilePath, contentType, contentId, userSlug || undefined)
      : ''

  const logShareEvent = (channel: string) => {
    if (!userSlug) return
    logShare({
      variables: {
        input: {
          content_type: contentType,
          content_id: contentId,
          share_channel: channel,
          ref_slug: userSlug,
        },
      },
    }).catch(() => {})
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link copied')
      logShareEvent('clipboard')
    } catch {
      toast.error('Failed to copy')
    }
  }

  const shareToInstagram = async () => {
    setIgBusy(true)
    try {
      const profileSlug = profile.profilePath.split('/').filter(Boolean).pop()?.split('?')[0]
      if (!profileSlug) throw new Error('Could not determine profile slug')
      const ogEndpoint = rawType === 'general' ? 'member' : 'listing'
      const ogParams = new URLSearchParams({ size: 'square' })
      if (userSlug) ogParams.set('ref', userSlug)
      const ogUrl = `/api/og/${ogEndpoint}/${profileSlug}?${ogParams.toString()}`
      let res = await fetch(ogUrl)
      if (!res.ok) res = await fetch(ogUrl)
      if (!res.ok) throw new Error('Image generation failed')
      const blob = await res.blob()
      const fileName = `${profile.profilePath.split('/').pop() || 'profile'}.png`
      const file = new File([blob], fileName, { type: 'image/png' })

      const canWebShare =
        isTouchDevice &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })

      if (canWebShare) {
        await navigator.share({ files: [file], title: profile.name, url: shareUrl })
        logShareEvent('instagram_web_share')
      } else {
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(objectUrl)
        toast.success('Image downloaded — upload it to Instagram')
        logShareEvent('instagram_download')
      }
    } catch (err) {
      if ((err as DOMException)?.name !== 'AbortError') {
        toast.error('Failed to prepare image')
      }
    } finally {
      setIgBusy(false)
    }
  }

  const openIntent = (channel: ChannelKey, url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
    logShareEvent(channel)
  }

  const intentUrl = (channel: Exclude<ChannelKey, 'instagram'>): string => {
    const encodedUrl = encodeURIComponent(shareUrl)
    const subject = encodeURIComponent(`Check out ${profile.name} on Culineer`)
    const text = `Check out ${profile.name} on Culineer`
    const encodedText = encodeURIComponent(text)
    const body = encodeURIComponent(`${text}\n\n${shareUrl}`)
    switch (channel) {
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
      case 'twitter':
        return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
      case 'gmail':
        return `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`
      case 'outlook':
        return `https://outlook.live.com/owa/?path=/mail/action/compose&subject=${subject}&body=${body}`
      case 'sms':
        return `sms:?body=${encodedText}%20${encodedUrl}`
    }
  }

  const allChannels: { key: ChannelKey; label: string; Icon: React.ElementType; color: string; touchOnly?: boolean }[] = [
    { key: 'instagram', label: 'Instagram', Icon: FaInstagram, color: '#E4405F' },
    { key: 'facebook', label: 'Facebook', Icon: FaFacebookF, color: '#1877F2' },
    { key: 'twitter', label: 'X', Icon: FaXTwitter, color: '#000000' },
    { key: 'linkedin', label: 'LinkedIn', Icon: FaLinkedinIn, color: '#0A66C2' },
    { key: 'gmail', label: 'Gmail', Icon: SiGmail, color: '#EA4335' },
    { key: 'outlook', label: 'Outlook', Icon: SiMicrosoftoutlook, color: '#0078D4' },
    { key: 'sms', label: 'Messages', Icon: MessageCircle, color: '#34C759', touchOnly: true },
  ]
  const channels = allChannels.filter((c) => !c.touchOnly || isTouchDevice)

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-center">Share {profile.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <h4 className="mb-2 text-sm text-grayText">Profile link</h4>
            <div className="flex gap-2 rounded-lg border border-[#e5e7eb] p-2">
              <Input value={shareUrl} readOnly className="border-none" />
              <Button variant="ghost" size="icon" onClick={copyLink} aria-label="Copy link">
                <Copy className="h-4 w-4 text-[#6eac36]" />
              </Button>
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-sm text-grayText">Share to</h4>
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-7">
              {channels.map(({ key, label, Icon, color }) => {
                const busy = key === 'instagram' && igBusy
                const onClick = () => {
                  if (busy) return
                  if (key === 'instagram') {
                    shareToInstagram()
                  } else if (key === 'facebook' && isTouchDevice) {
                    const text = `Check out ${profile.name} on Culineer`
                    navigator.share({ url: shareUrl, title: profile.name, text })
                      .then(() => logShareEvent('facebook_web_share'))
                      .catch((err) => { if ((err as DOMException)?.name !== 'AbortError') toast.error('Failed to share') })
                  } else {
                    openIntent(key, intentUrl(key as Exclude<ChannelKey, 'instagram'>))
                  }
                }
                return (
                  <button
                    key={key}
                    onClick={onClick}
                    disabled={busy}
                    aria-label={`Share to ${label}`}
                    title={label}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e5e7eb] transition hover:scale-105 hover:shadow disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <Icon className="h-5 w-5" style={{ color }} />
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
